import { useMemo } from 'react';
import { useIsAdmin } from './authentication/authentication-service';
import { Location, useGetLocationListQuery, useGetLocationQuery, useGetMeQuery } from './gql/graphql.generated';
import { useLocationService } from './location/LocationService';

export const useMyLocations = () => {
  const [{ data: myData }] = useGetMeQuery();
  const [{ data: locationsData }] = useGetLocationListQuery();

  const myLocations = myData?.myEmployee?.locations || [];
  const allLocations = locationsData?.locations || [];
  const myId = myData?.myEmployee?.identityId;

  const isAdmin = useIsAdmin();

  return useMemo(() => {
    return allLocations.filter(location => {
      if (myLocations.find(myLoc => myLoc.id === location.id) || isAdmin) {
        return true;
      }

      const properties = JSON.parse(location.properties || '{}');
      if (properties.supervisorId === myId) {
        return true;
      }

      return false;
    });
  }, [allLocations, myLocations, isAdmin, myId]);
};

export const useAllLocations = () => {
  const [{ data: locationsData }] = useGetLocationListQuery();
  return (locationsData?.locations || []) as Location[];
};

export const useMyLocation = () => {
  const [{ data: myData }] = useGetMeQuery();
  const selectedLocation = useLocationService(state => state.selectedLocation);

  if (selectedLocation) {
    return selectedLocation as Location;
  }

  const myLocationId = myData!.myEmployee?.homeLocationId;

  if (!myLocationId) {
    throw Error('User is not assigned to a location');
  }

  const [{ data: locationData }] = useGetLocationQuery({
    variables: { id: myLocationId }
  });

  const location = locationData!.location;

  if (!location) {
    throw Error("User's location not found");
  }

  return location as Location;
};

export const useLocationDetails = (locationId: number) => {
  const locations = useMyLocations();

  const selectedLocation = locations.find(location => location.id === locationId);

  return selectedLocation as Location;
};

export const useLocation = (locationId: number) => {
  const [{ data, fetching, error }, refetch] = useGetLocationQuery({
    variables: { id: locationId }
  });

  const selectedLocation = data?.location;

  if (!selectedLocation) {
    throw new Error(`Location with ID ${locationId} not found`);
  }

  return {
    location: selectedLocation as Location,
    fetching,
    error,
    refetch
  };
};
